import React from 'react';
import { Rect, Circle, Group, RegularPolygon, Transformer } from 'react-konva';

export type ShapeType = 'rect' | 'circle' | 'square' | 'triangle';

export interface KonvaShapeProps {
  id: string;
  type: ShapeType;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  isSelected?: boolean;
  onMove: (id: string, x: number, y: number) => void;
  onSelect?: (id: string) => void;
  onResize?: (id: string, width: number, height: number) => void;
}

const KonvaShape: React.FC<KonvaShapeProps> = ({
  id,
  type,
  x,
  y,
  width,
  height,
  color,
  isSelected = false,
  onMove,
  onSelect,
  onResize,
}) => {
  const groupRef = React.useRef<any>(null);
  const trRef = React.useRef<any>(null);

  React.useEffect(() => {
    if (isSelected && trRef.current) {
      trRef.current.nodes([groupRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  const handleTransformEnd = () => {
    const node = groupRef.current;
    let baseW,baseH;
    if(type==='circle'){
      const c = node.findOne('Circle');
      baseW=c.radius()*2;
      baseH=baseW;
    } else if(type==='triangle'){
      const tri = node.findOne('RegularPolygon');
      baseW=tri.radius()*2;
      baseH=baseW;
    } else {
      const r=node.findOne('Rect');
      baseW=r.width(); baseH=r.height();
    }
    const newWidth = Math.max(40, baseW * node.scaleX());
    const newHeight = Math.max(40, baseH * node.scaleY());
    // apply to children shapes for immediate visual update
    if(type==='circle'){
      node.findOne('Circle').radius(newWidth/2);
    } else if(type==='triangle'){
      node.findOne('RegularPolygon').radius(newWidth/2);
    } else { // rect / square
      const rect=node.findOne('Rect');
      rect.width(newWidth);
      rect.height(type==='square'?newWidth:newHeight);
    }
    node.scale({x:1,y:1});
    onResize && onResize(id, newWidth, newHeight);
  };

  return (
    <>
    <Group
      ref={groupRef}
      x={x}
      y={y}
      draggable
      onDragEnd={(e) => onMove(id, e.target.x(), e.target.y())}
      onClick={() => onSelect && onSelect(id)}
      onTransformEnd={handleTransformEnd}
    >
      {type === 'rect' || type === 'square' ? (
        <Rect
          width={width}
          height={type==='square'? width: height}
          fill={color}
          stroke={isSelected ? '#3b82f6' : undefined}
          strokeWidth={isSelected ? 2 : 0}
          cornerRadius={4}
          shadowBlur={4}
        />
      ) : type==='circle' ? (
        <Circle
          radius={width / 2}
          fill={color}
          stroke={isSelected ? '#3b82f6' : undefined}
          strokeWidth={isSelected ? 2 : 0}
          shadowBlur={4}
        />
      ) : (
        <RegularPolygon
          sides={3}
          radius={width/2}
          fill={color}
          stroke={isSelected ? '#3b82f6' : undefined}
          strokeWidth={isSelected ? 2 : 0}
          shadowBlur={4}
          rotation={30}
        />
      )}
    </Group>
    {isSelected && (
      <Transformer ref={trRef} boundBoxFunc={(oldBox, newBox)=>{ if(newBox.width<20 || newBox.height<20){ return oldBox;} return newBox; }} />
    )}
    </>
  );
};

export default KonvaShape; 